//customer details, service locations, customer discount and change password
import { customStyle, REGEX } from "components/Common/constants";
import React, { useEffect, useRef, useState } from "react";
import {
  Card,
  CardBody,
  Row,
  Col,
  Badge,
  Nav,
  NavItem,
  TabContent,
  TabPane,
  NavLink,
  Container,
  Button,
  Input,
  InputGroup,
  InputGroupText,
  FormGroup,
  Label,
  InputGroupAddon,
} from "reactstrap";
import Select from "react-select";
import { useLocation, useParams } from "react-router-dom";
import ServiceLocations from "./ServiceLocations";
import CustomerDiscount from "./CustomerDiscount";
import ChangePassword from "./ChangePassword";
import { showAlert } from "components/Common/commonFunctions";
import NotificationAlert from "react-notification-alert";
import {
  getPaymentTermList,
  getCustomerById,
  editCustomer,
} from "actions/customer";
import Breadcrumbs from "components/Common/BreadCrumbs";
import { useDispatch, useSelector } from "react-redux";
import { urlDecodedId } from "components/Common/encodeDecodeFunction";
import SubUsers from "./SubUsers";

function CustomerDetails() {
  const { id: encodedId } = useParams();
  const decodedId = urlDecodedId(encodedId);
  const location = useLocation();
  const notificationAlertRef = useRef(null);
  const dispatch = useDispatch();
  const data = location.state || {};

  //common states
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);
  const [showAlternative, setShowAlternative] = useState({
    loader: false,
    error: "",
  });

  //customer detail states
  const [customerData, setCustomerData] = useState(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNo: "",
    address: "",
    vatID: "",
    paymentTerm: null,
    clientID: "",
    status: "",
    companyName: "",
  });
  const query = new URLSearchParams(location.search);
  const urlTab = query.get("tab");
  const [activeTab, setActiveTab] = useState(urlTab || "details");
  const isEditable = data?.isEditable || false;
  const [isEditing, setIsEditing] = useState(isEditable);
  const paymentTermOptions = useSelector(
    (state) => state.customer.paymentTermList?.data?.paymentTerm || [],
  );

  useEffect(() => {
    getPaymentTermList(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (!decodedId || activeTab !== "details") return;

    const fetchCustomerData = async () => {
      setShowAlternative({ loader: true, error: "" });

      const res = await getCustomerById(decodedId);
      if (res.code === 200) {
        setCustomerData(res.data);
        setShowAlternative({ loader: false, error: "" });
      } else {
        setShowAlternative({
          loader: false,
          error: res.message || "Failed to load customer details",
        });
      }
    };

    fetchCustomerData();
  }, [decodedId, activeTab]);

  useEffect(() => {
    if (customerData) {
      setFormData({
        firstName: customerData.firstName || "",
        lastName: customerData.lastName || "",
        email: customerData.email || "",
        phoneNo: customerData.phoneNo || "",
        address: customerData.address || "",
        vatID: customerData.vatID || "",
        paymentTerm: customerData.paymentTerm?.ID || null,
        clientID: customerData.clientID || "",
        status: customerData.status || "",
        companyName: customerData.companyName || "",
      });
    }
  }, [customerData]);

  const runSaveWithLoader = async (callback) => {
    setShowLoaderOnClick(true);
    try {
      await callback();
    } finally {
      setShowLoaderOnClick(false);
    }
  };

  const getCustomerDetails = async (id) => {
    try {
      setShowAlternative({ loader: true, error: "" });

      const res = await getCustomerById(id);

      if (res.code === 200) {
        setCustomerData(res.data);

        setFormData({
          firstName: res.data.firstName || "",
          lastName: res.data.lastName || "",
          email: res.data.email || "",
          phoneNo: res.data.phoneNo || "",
          address: res.data.address || "",
          vatID: res.data.vatID || "",
          paymentTerm: res.data.paymentTerm?.ID || null,
          clientID: res.data.clientID || "",
          status: res.data.status || "",
          companyName: res.data.companyName || "",
        });

        setShowAlternative({ loader: false, error: "" });
      } else {
        const message = Array.isArray(res?.message)
          ? res?.message[0]?.Msg
          : res?.message || "Failed to load customer details";

        setShowAlternative({ loader: false, error: message });
      }

      return res;
    } catch (error) {
      setShowAlternative({
        loader: false,
        error: error?.message || "Failed to load customer details",
      });

      return { code: 500, message: error?.message };
    }
  };

  const notify = (type, message) => {
    const options = {
      place: "tr",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    };
    notificationAlertRef.current.notificationAlert(options);
  };

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);

      const searchParams = new URLSearchParams(location.search);
      searchParams.set("tab", tab);

      window.history.replaceState(
        null,
        "",
        `${location.pathname}?${searchParams.toString()}`,
      );
    }
    setIsEditing(false); //resets editing mode of customer details section
  };

  const toggleEditMode = () => {
    setIsEditing(!isEditing);
    // Always reset formData to customerData, ensuring paymentTerm is correctly mapped to its ID
    setFormData({
      ...customerData,
      paymentTerm: customerData.paymentTerm?.ID || null,
    });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      [`${name}Error`]: "",
    }));
  };

  const handleSelectChange = (selectedOption) => {
    setFormData((prev) => ({
      ...prev,
      paymentTerm: selectedOption ? selectedOption.value : null,
      paymentTermError: "",
    }));
  };

  const buildEditPayload = () => {
  const payload = {
    firstName: formData.firstName,
    lastName: formData.lastName,
    email: formData.email,
    phoneNo: formData.phoneNo,
    address: formData.address,
    vatID: formData.vatID,
    paymentTermId: Number(formData.paymentTerm),
    status: customerData.status,
    companyName: formData.companyName,
  };

  // Only send clientID when it has a value
  if (formData.clientID?.trim()) {
    payload.clientID = formData.clientID.trim();
  }

  return payload;
};

  const handleSave = async () => {
    await runSaveWithLoader(async () => {
      const payload = buildEditPayload();

      let hasError = false;

      const map = {
        firstName: "firstName",
        lastName: "lastName",
        email: "email",
        phoneNo: "phoneNo",
        vatID: "vatID",
        paymentTermId: "paymentTerm",
        companyName: "companyName",
      };

      Object.entries(map).forEach(([payloadKey, formKey]) => {
        const value = payload[payloadKey];
        const error = getValidationError(formKey, value);

        if (error) {
          hasError = true;
          setFormData((prev) => ({
            ...prev,
            [`${formKey}Error`]: error,
          }));
        }
      });

      if (hasError) {
        return;
      }

      const res = await editCustomer(customerData.ID, payload);

      if (res.code === 200) {
        notify("success", res?.message || "Customer updated successfully.");

        const freshData = await getCustomerById(customerData.ID);
        if (freshData.code === 200) {
          setCustomerData(freshData.data);
        }

        setIsEditing(false);
      } else {
        const errorMsg = Array.isArray(res.message)
          ? res.message.map((e) => `${e.Field}: ${e.Msg}`).join(", ")
          : res.message;

        notify("danger", errorMsg || "Failed to update customer");
      }
    });
  };

  const hasAnyError = (data) => {
    const nestedErrors = Object.values(data).some(
      (field) =>
        field && typeof field === "object" && "error" in field && field.error,
    );

    if (nestedErrors) return true;

    const flatErrors = Object.entries(data).some(
      ([key, value]) => key.endsWith("Error") && value,
    );

    return flatErrors;
  };

  const handleStatusConfirm = async (id, newStatus) => {
    const payload = { status: newStatus };

    const res = await editCustomer(id, payload);

    if (res.code === 200) {
      notify("success", `Customer status updated to ${newStatus}.`);
      await getCustomerDetails(id);
    } else {
      notify("danger", "Failed to update customer status");
    }
  };

  const handleStatusChange = (item) => {
    const isActive = item.status === "ACTIVE";
    const newStatus = isActive ? "SUSPENDED" : "ACTIVE";
    showAlert({
      title: `Are you sure you want to ${isActive ? "suspend" : "activate"
        } this customer?`,
      message: `Customer "${item.firstName} ${item.lastName}" will be ${isActive ? "suspended" : "activated"
        }.`,
      type: "warning",
      confirmStyle: "default",
      cancelStyle: "secondary",
      confirmText: "Yes",
      cancelText: "Cancel",
      onConfirm: () => handleStatusConfirm(item.ID, newStatus),
      onCancel: () => { },
    });
  };

  const getValidationError = (name, value) => {
    switch (name) {
      case "firstName":
        return !value
          ? "Please enter customer first name"
          : value.length < 2
            ? "First name must be at least 2 characters"
            : value.length > 50
              ? "First name must be less than 50 characters"
              : "";

      case "lastName":
        return !value
          ? "Please enter customer last name"
          : value.length < 2
            ? "Last name must be at least 2 characters"
            : value.length > 50
              ? "Last name must be less than 50 characters"
              : "";

      case "name":
        return !value
          ? "Please enter contact name"
          : value.length < 2
            ? "Contact name must be at least 2 characters"
            : value.length > 100
              ? "Contact name must be less than 100 characters"
              : "";

      case "email":
        return !value
          ? "Please enter email address"
          : value.length > 100
            ? "Email must be less than 100 characters"
            : !REGEX.EMAIL_REGEX.test(value)
              ? "Please enter a valid email address"
              : "";

      case "phoneNo":
      case "mobile":
        return !value
          ? "Please enter contact number"
          : !REGEX.CONTACT_REGEX.test(value)
            ? "Please enter a valid 9-digit contact number"
            : "";

      // case "vatID":
      //   return !value
      //     ? "Please enter VAT ID"
      //     : !REGEX.VAT_ID_REGEX.test(value)
      //       ? "Please enter a valid 15 digits vat id"
      //       : "";

      case "clientID":
        return !value
          ? "Please enter Client ID"
          : value.length > 100
            ? "Client ID must be less than 100 characters"
            : "";

      case "outlet":
        return !value
          ? "Please enter outlet name"
          : value.length < 2
            ? "Outlet must be at least 2 characters"
            : "";

      case "region":
        return !value ? "Please select region" : "";

      case "city":
        return !value ? "Please select city" : "";

      case "address":
        return !value
          ? "Please enter address"
          : value.length < 2
            ? "Address must be at least 2 characters"
            : "";

      case "postCode":
        return !value
          ? "Please enter post code"
          : !REGEX.POSTAL_CODE_REGEX.test(value)
            ? "Please enter a valid post code (5 or 9 digits, with optional hyphen)"
            : "";

      case "alt_email":
        return value && value.length > 100
          ? "Alternative email must be less than 100 characters"
          : value && !REGEX.EMAIL_REGEX.test(value)
            ? "Please enter a valid alternative email address"
            : "";

      case "alt_mobile":
        return value && !REGEX.CONTACT_REGEX.test(value)
          ? "Please enter a valid 9-digit alternative contact number"
          : "";

      case "companyName":
        return value && value.trim().length < 2
          ? "Company name must be at least 2 characters"
          : value && value.trim().length > 200
            ? "Company name must be less than 200 characters"
            : "";
      default:
        return "";
    }
  };

  const handleBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);

    setFormData((prev) => ({
      ...prev,
      [`${name}Error`]: error,
    }));
  };

  return (
    <>
      <div className="rna-container">
        <NotificationAlert ref={notificationAlertRef} />
      </div>
      <Container fluid className="mt-4">
        <Breadcrumbs
          items={[
            { name: "Customers", link: "/admin/customers" },
            {
              name:
                `${`${customerData?.firstName || ""} ${customerData?.lastName || ""}`.trim()}` ||
                "Customer Details",
              link: `/admin/customers/${encodedId}/details?tab=${activeTab}`,
            },
          ]}
        />

        <Row>
          <Card className="m-3 shadow-sm border-0">
            {showAlternative.loader ? (
              <div className="d-flex justify-content-center align-items-center w-100 my-5 py-5">
                <i className="fa fa-spinner fa-spin fa-2xl" />
              </div>
            ) : showAlternative.error ? (
              <div className="loaderContainer text-muted d-flex flex-column">
                <h4 className="text-muted">No Customer Data Available</h4>
                <span className="validation-error">
                  {showAlternative.error}
                </span>
              </div>
            ) : (
              <>
                <Nav className="custom-tabs">
                  <NavItem>
                    <NavLink
                      className={`text-center ${activeTab === "details" ? "active" : ""
                        }`}
                      href="#"
                      onClick={() => toggleTab("details")}
                    >
                      Customer Details
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`text-center pl-3 ${activeTab === "serviceLocation" ? "active" : ""
                        }`}
                      href="#"
                      onClick={() => toggleTab("serviceLocation")}
                    >
                      Service Locations
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`text-center ${activeTab === "subUsers" ? "active" : ""
                        }`}
                      href="#"
                      onClick={() => toggleTab("subUsers")}
                    >
                      Sub Users
                    </NavLink>
                  </NavItem>
                  <NavItem>
                    <NavLink
                      className={`text-center ${activeTab === "discount" ? "active" : ""
                        }`}
                      href="#"
                      onClick={() => toggleTab("discount")}
                    >
                      Customer Discount
                    </NavLink>
                  </NavItem>

                  <NavItem>
                    <NavLink
                      className={`text-center ${activeTab === "password" ? "active" : ""
                        }`}
                      href="#"
                      onClick={() => toggleTab("password")}
                    >
                      Change Password
                    </NavLink>
                  </NavItem>
                </Nav>

                <CardBody>
                  <TabContent activeTab={activeTab}>
                    <TabPane tabId="details">
                      <Row className="justify-content-center">
                        <Col md={12} lg={12}>
                          <Row className="d-flex justify-content-end mr-2 mb-3">
                            {!isEditing && (
                              <>
                                <Button
                                  color="default"
                                  size="sm"
                                  onClick={toggleEditMode}
                                >
                                  <i className="fa fa-pencil mr-2" />
                                  Edit
                                </Button>

                                <Button
                                  className="ml-3"
                                  color="secondary"
                                  size="sm"
                                  onClick={() =>
                                    customerData &&
                                    handleStatusChange(customerData)
                                  }
                                >
                                  <i
                                    className={`fa ${formData.status === "ACTIVE"
                                        ? "fa-user-slash"
                                        : formData.status === "SUSPENDED"
                                          ? "fa-user"
                                          : ""
                                      } mr-2`}
                                  />
                                  {formData.status === "ACTIVE"
                                    ? "Suspend"
                                    : formData.status === "SUSPENDED"
                                      ? "Activate"
                                      : ""}
                                </Button>
                              </>
                            )}
                          </Row>

                          <Label className="customer-title px-3">
                            Company Details
                          </Label>
                          <Row className="px-3 mt-3">
                            <Col md={4} className="mb-1">
                              <FormGroup
                                className={`has-label ${formData.companyNameError ? "has-danger" : ""
                                  } d-flex flex-column align-items-start`}
                              >
                                <Label>Company Name </Label>
                                {isEditing ? (
                                  <Input
                                    name="companyName"
                                    type="text"
                                    placeholder="Enter company name"
                                    value={formData.companyName}
                                    onChange={handleInputChange}
                                    className={
                                      formData.companyNameError
                                        ? "is-invalid"
                                        : ""
                                    }
                                    maxLength={200}
                                    onBlur={(e) =>
                                      handleBlur(e.target.name, e.target.value)
                                    }
                                  />
                                ) : (
                                  <span className="small-text">
                                    {customerData?.companyName || "-"}
                                  </span>
                                )}
                                {formData.companyNameError && (
                                  <small className="validation-error">
                                    {formData.companyNameError}
                                  </small>
                                )}
                              </FormGroup>
                            </Col>

                            <Col md={4} className="mb-1">
                              <FormGroup className="d-flex flex-column align-items-start">
                                <Label>Address</Label>
                                {isEditing ? (
                                  <Input
                                    name="address"
                                    type="text"
                                    placeholder="Enter address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    maxLength={100}
                                  />
                                ) : (
                                  <span className="small-text">
                                    {customerData?.address || "-"}
                                  </span>
                                )}
                              </FormGroup>
                            </Col>

                            <Col md={4} className="mb-1">
                              <FormGroup
                                className={`has-label ${formData.vatIDError ? "has-danger" : ""
                                  } d-flex flex-column align-items-start`}
                              >
                                <Label>
                                  VAT ID{" "}
                                  {/* {isEditing && (
                                    <span className="validation-error">*</span>
                                  )} */}
                                </Label>
                                {isEditing ? (
                                  <Input
                                    name="vatID"
                                    type="text"
                                    placeholder="Enter VAT ID"
                                    value={formData.vatID}
                                    onChange={handleInputChange}
                                    className={
                                      formData.vatIDError ? "is-invalid" : ""
                                    }
                                    onBlur={(e) =>
                                      handleBlur(e.target.name, e.target.value)
                                    }
                                    maxLength={15}
                                  />
                                ) : (
                                  <span className="small-text">
                                    {customerData?.vatID}
                                  </span>
                                )}
                                {formData.vatIDError && (
                                  <small className="validation-error">
                                    {formData.vatIDError}
                                  </small>
                                )}
                              </FormGroup>
                            </Col>
                          </Row>

                          <hr />

                          <Label className="customer-title px-3">
                            Account Owner
                          </Label>
                          <Row className="px-3 mt-3">
                            <Col md={4} className="mb-2">
                              <FormGroup
                                className={`has-label ${formData.firstNameError ? "has-danger" : ""
                                  } d-flex flex-column align-items-start`}
                              >
                                <Label>
                                  First Name{" "}
                                  {isEditing && (
                                    <span className="validation-error">*</span>
                                  )}
                                </Label>
                                {isEditing ? (
                                  <Input
                                    name="firstName"
                                    type="text"
                                    placeholder="Enter first name"
                                    value={formData.firstName}
                                    onChange={handleInputChange}
                                    className={
                                      formData.firstNameError
                                        ? "is-invalid"
                                        : ""
                                    }
                                    maxLength={50}
                                    onBlur={(e) =>
                                      handleBlur(e.target.name, e.target.value)
                                    }
                                  />
                                ) : (
                                  <span className="small-text text-wrap">
                                    {customerData?.firstName}
                                  </span>
                                )}
                                {formData.firstNameError && (
                                  <small className="validation-error">
                                    {formData.firstNameError}
                                  </small>
                                )}
                              </FormGroup>
                            </Col>

                            <Col md={4} className="mb-2">
                              <FormGroup
                                className={`has-label ${formData.lastNameError ? "has-danger" : ""
                                  } d-flex flex-column align-items-start`}
                              >
                                <Label>
                                  Last Name{" "}
                                  {isEditing && (
                                    <span className="validation-error">*</span>
                                  )}
                                </Label>
                                {isEditing ? (
                                  <Input
                                    name="lastName"
                                    type="text"
                                    placeholder="Enter last name"
                                    value={formData.lastName}
                                    onChange={handleInputChange}
                                    className={
                                      formData.lastNameError ? "is-invalid" : ""
                                    }
                                    maxLength={50}
                                    onBlur={(e) =>
                                      handleBlur(e.target.name, e.target.value)
                                    }
                                  />
                                ) : (
                                  <span className="small-text text-wrap">
                                    {customerData?.lastName}
                                  </span>
                                )}
                                {formData.lastNameError && (
                                  <small className="validation-error">
                                    {formData.lastNameError}
                                  </small>
                                )}
                              </FormGroup>
                            </Col>

                            <Col md={4} className="mb-2">
                              <FormGroup
                                className={`has-label ${formData.emailError ? "has-danger" : ""
                                  } d-flex flex-column align-items-start`}
                              >
                                <Label>
                                  Email Address{" "}
                                  {isEditing && (
                                    <span className="validation-error">*</span>
                                  )}
                                </Label>
                                {isEditing ? (
                                  <Input
                                    name="email"
                                    type="email"
                                    placeholder="Enter email address"
                                    value={formData.email}
                                    className={
                                      formData.emailError ? "is-invalid" : ""
                                    }
                                    onChange={handleInputChange}
                                    maxLength={100}
                                    onBlur={(e) =>
                                      handleBlur(e.target.name, e.target.value)
                                    }
                                  />
                                ) : (
                                  <span className="small-text text-wrap">
                                    {customerData?.email}
                                  </span>
                                )}
                                {formData.emailError && (
                                  <small className="validation-error">
                                    {formData.emailError}
                                  </small>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={4} className="mb-2">
                              <FormGroup
                                className={`has-label ${formData.phoneNoError ? "has-danger" : ""
                                  } d-flex flex-column align-items-start`}
                              >
                                <Label>
                                  Contact Number{" "}
                                  {isEditing && (
                                    <span className="validation-error">*</span>
                                  )}
                                </Label>
                                {isEditing ? (
                                  <InputGroup className="input-group">
                                    <InputGroupAddon addonType="prepend">
                                      <InputGroupText className="d-flex align-items-center">
                                        <span className="mr-1">🇸🇦</span> +966
                                      </InputGroupText>
                                    </InputGroupAddon>
                                    <Input
                                      name="phoneNo"
                                      type="tel"
                                      placeholder="Enter contact number"
                                      autoComplete="off"
                                      value={formData.phoneNo}
                                      className={
                                        formData.phoneNoError
                                          ? "is-invalid"
                                          : ""
                                      }
                                      onChange={handleInputChange}
                                      maxLength={9}
                                      onBlur={(e) =>
                                        handleBlur(
                                          e.target.name,
                                          e.target.value,
                                        )
                                      }
                                    />
                                  </InputGroup>
                                ) : (
                                  <span className="small-text text-wrap">
                                    {"+966"}{" "}{customerData?.phoneNo}
                                  </span>
                                )}
                                {formData.phoneNoError && (
                                  <small className="validation-error">
                                    {formData.phoneNoError}
                                  </small>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={4} className="mb-2">
                              <FormGroup className="d-flex flex-column align-items-start mt-2">
                                <Label>Address</Label>
                                {isEditing ? (
                                  <Input
                                    name="address"
                                    type="text"
                                    placeholder="Enter address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    maxLength={100}
                                  />
                                ) : (
                                  <span className="small-text">
                                    {customerData?.address || "-"}
                                  </span>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={4} className="mb-2">
                              <FormGroup
                                className={`has-label ${formData.paymentTermError ? "has-danger" : ""
                                  } d-flex flex-column align-items-start mt-2`}
                              >
                                <Label>
                                  Payment Term{" "}
                                  {isEditing && (
                                    <span className="validation-error">*</span>
                                  )}
                                </Label>
                                {isEditing ? (
                                  <Select
                                    name="paymentTerm"
                                    className="text-left"
                                    placeholder="Select payment term"
                                    options={paymentTermOptions.map((t) => ({
                                      value: t.ID,
                                      label: t.term,
                                    }))}
                                    classNamePrefix="react-select"
                                    styles={customStyle}
                                    value={paymentTermOptions
                                      .map((t) => ({
                                        value: t.ID,
                                        label: t.term,
                                      }))
                                      .find(
                                        (option) =>
                                          option.value === formData.paymentTerm,
                                      )}
                                    onChange={(selectedOption) =>
                                      handleSelectChange(selectedOption)
                                    }
                                    onBlur={() =>
                                      handleBlur(
                                        "paymentTerm",
                                        formData.paymentTerm,
                                      )
                                    }
                                  />
                                ) : (
                                  <span className="small-text text-wrap">
                                    {paymentTermOptions.find(
                                      (pt) =>
                                        pt.ID === customerData?.paymentTerm?.ID,
                                    )?.term || ""}
                                  </span>
                                )}
                                {formData.paymentTermError && (
                                  <small className="validation-error">
                                    {formData.paymentTermError}
                                  </small>
                                )}
                              </FormGroup>
                            </Col>
                            <Col md={4} className="mb-0">
                              <FormGroup
                                className={`has-label ${formData.clientIDError ? "has-danger" : ""
                                  } d-flex flex-column align-items-start`}
                              >
                                <Label>
                                  Client ID{" "}
                                  {/* {isEditing && (
                                    <span className="validation-error">*</span>
                                  )} */}
                                </Label>
                                {/* {isEditing ? (
                                  <Input
                                    name="clientID"
                                    type="text"
                                    placeholder="Enter client ID"
                                    value={formData.clientID}
                                    className={
                                      formData.clientIDError ? "is-invalid" : ""
                                    }
                                    onChange={handleInputChange}
                                    onBlur={(e) =>
                                      handleBlur(e.target.name, e.target.value)
                                    }
                                    maxLength={100}
                                  />
                                ) : ( */}
                                  <span className="small-text text-wrap">
                                    {customerData?.clientID}
                                  </span>
                                {/* // )} */}
                                {/* {formData.clientIDError && (
                                  <small className="validation-error">
                                    {formData.clientIDError}
                                  </small>
                                )} */}
                              </FormGroup>
                            </Col>
                            <Col md={4} className="mb-0">
                              <FormGroup
                                className={`d-flex flex-column align-items-start`}
                              >
                                <Label>Status</Label>
                                <span>
                                  <Badge
                                    className="px-3 py-2"
                                    color={
                                      customerData?.status === "ACTIVE"
                                        ? "success"
                                        : customerData?.status === "SUSPENDED"
                                          ? "warning"
                                          : "secondary"
                                    }
                                    pill
                                  >
                                    {customerData?.status === "SUSPENDED"
                                      ? "suspended"
                                      : customerData?.status}
                                  </Badge>
                                </span>
                              </FormGroup>
                            </Col>
                          </Row>
                          <Row>
                            <Col className="d-flex justify-content-end">
                              {isEditing && (
                                <>
                                  <Button
                                    className="mr-2"
                                    color="secondary"
                                    size="sm"
                                    onClick={toggleEditMode}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    color="default"
                                    onClick={handleSave}
                                    disabled={hasAnyError(formData)}
                                  >
                                    {showLoaderOnClick ? (
                                      <i className="fa fa-spinner fa-spin" />
                                    ) : (
                                      "Save"
                                    )}
                                  </Button>
                                </>
                              )}
                            </Col>
                          </Row>
                        </Col>
                      </Row>
                    </TabPane>
                    <TabPane tabId="serviceLocation">
                      <ServiceLocations
                        customerId={decodedId}
                        notify={notify}
                        getValidationError={getValidationError}
                        hasAnyError={hasAnyError}
                        activeTab={activeTab}
                      />
                    </TabPane>
                    <TabPane tabId="discount">
                      <CustomerDiscount
                        customerId={decodedId}
                        notify={notify}
                        hasAnyError={hasAnyError}
                        activeTab={activeTab}
                      />
                    </TabPane>

                    <TabPane tabId="password">
                      <ChangePassword
                        customerId={decodedId}
                        notify={notify}
                        hasAnyError={hasAnyError}
                        activeTab={activeTab}
                      />
                    </TabPane>

                    <TabPane tabId="subUsers">
                      <SubUsers
                        customerId={decodedId}
                        notify={notify}
                        hasAnyError={hasAnyError}
                        activeTab={activeTab}
                      />
                    </TabPane>
                  </TabContent>
                </CardBody>
              </>
            )}
          </Card>
        </Row>
      </Container>
    </>
  );
}

export default CustomerDetails;
